// Vendor imports
const classNames = require('classnames')
const PropTypes = require('prop-types')
const React = require('react')
// Shared imports
const BemEntity = require('shared/bem')
const { formatCount } = require('shared/string-utils')
const {
  TYPES: modelTypes,
  ARTICLE_SUBTYPES: articleSubtypes,
  ARTICLE_SUBTYPES_KEYS: articleSubTypesKeys
} = require('shared/model-types')
// Common client-V2 imports
const tag = require('client-v2/common/utils/hyperscript')
const { CircleButton } = require('client-v2/common/components/buttons')
const EmbeddedPlayer = require('client-v2/common/components/embedded-player')
const LazyImage = require('client-v2/common/components/lazy-image')
const Portal = require('client-v2/common/components/portal')
const UserAvatar = require('client-v2/common/components/user-avatar')
const { registerComponentEventHandlers } = require('client-v2/common/components/utils')
const { RecReasonRecord } = require('client-v2/common/containers/feed/records')
const ArticleMixButton = require('client-v2/common/containers/lightweight-engagement/components/article-mix-button')
const AddToCollectionModalAnchor = require('client-v2/common/containers/lightweight-engagement/containers/add-to-collection-modal/components/anchor-smart')
const {
  articleProfileHref,
  articleDomainSearchHref
} = require('client-v2/common/models/article/actions')
const ArticleModelRecord = require('client-v2/common/models/article/record')
const PageContextRecord = require('client-v2/common/models/page-context/record')
const ViewportObserver = require('client-v2/common/utils/viewport-observer')

const bem = BemEntity('ArticleCard')

let fullyVisibleObserver
let partiallyVisibleObserver
if (typeof window === 'object') {
  fullyVisibleObserver = new ViewportObserver({
    callback: (domElement, reactElement) => {
      reactElement.setFullyVisible()
    },
    intersectionObserverOptions: {
      threshold: 1.00
    }
  })
  partiallyVisibleObserver = new ViewportObserver({
    callback: (domElement, reactElement) => {
      reactElement.setPartiallyVisible()
    },
    intersectionObserverOptions: {
      threshold: 0.1
    }
  })
}

class ArticleCard extends React.PureComponent {
  constructor(props) {
    super(props)

    this.ref = React.createRef()
    this.anchorId = Symbol()
    this.state = {
      isEmbedDisclosed: props.isEmbedDisclosed,
      isFullyVisible: false,
      isPartiallyVisible: false
    }

    // Ensure function references do not change between render calls
    registerComponentEventHandlers(this)
    this.stopPropagation = event => { event.stopPropagation() }
    this.onAddClick = this.onAddClick.bind(this)
    this.onMoreClick = this.onMoreClick.bind(this)
    this.discloseEmbed = this.discloseEmbed.bind(this)
  }

  // Component lifecycle

  componentDidMount() {
    this.observeVisibility()
  }

  componentWillUnmount() {
    this.unobserveVisibility()
  }

  // Visibility/seen methods

  observeVisibility() {
    const el = this.ref.current
    if (el) {
      if (!this.state.isFullyVisible && fullyVisibleObserver) {
        fullyVisibleObserver.observe(el, this)
      }
      if (!this.state.isPartiallyVisible && partiallyVisibleObserver) {
        partiallyVisibleObserver.observe(el, this)
      }
    }
  }

  unobserveVisibility() {
    const el = this.ref.current
    if (el) {
      if (fullyVisibleObserver) {
        fullyVisibleObserver.unobserve(el)
      }
      if (partiallyVisibleObserver) {
        partiallyVisibleObserver.unobserve(el)
      }
    }
  }

  setFullyVisible() {
    if (!this.state.isFullyVisible) {
      this.onSeen()
      this.setState(() => ({
        isFullyVisible: true,
        isPartiallyVisible: true
      }))
    }
  }

  setPartiallyVisible() {
    if (!this.state.isPartiallyVisible) {
      this.setState(() => ({
        isPartiallyVisible: true
      }))
    }
  }

  // Footer methods

  footerHtml() {
    const { recReason } = this.props
    const recReasonHtml = recReason && recReason.html
    return recReasonHtml ? { __html: recReasonHtml } : null
  }

  footerUser() {
    const { recReason } = this.props
    const firstRecReasonItem = recReason && recReason.items[0]
    if (firstRecReasonItem && firstRecReasonItem.type === modelTypes.USER) {
      return firstRecReasonItem
    }
  }

  // Event handler methods

  onAddClick(event) {
    event.stopPropagation()
    this.props.onAddClick({
      article: this.props.article,
      pageContext: this.props.pageContext,
      anchorId: this.anchorId
    })
  }

  onMoreClick(event) {
    event.stopPropagation()
    this.props.onMoreClick({
      article: this.props.article,
      pageContext: this.props.pageContext,
      anchorId: this.anchorId
    })
  }

  discloseEmbed() {
    if (!this.state.isEmbedDisclosed) {
      this.onEmbedDisclose()
      this.setState(() => ({
        isEmbedDisclosed: true
      }))
    }
  }

  // Linkage methods

  articleHref() {
    if (this.props.isHrefsEnabled) {
      return this.props.article.url
    }
  }

  domainHref() {
    if (this.props.isHrefsEnabled) {
      return articleDomainSearchHref(this.props.article)
    }
  }

  permalinkHref() {
    if (this.props.isHrefsEnabled) {
      return articleProfileHref(this.props.article)
    }
  }

  // Render methods

  renderAddButton() {
    if (!this.props.onAddClick) {
      return null
    }
    return (
      tag(AddToCollectionModalAnchor, {
        anchorId: this.anchorId,
        popperClassName: bem.element('add-button-popper'),
        portalContainer: this.props.portalContainer,
        urlId: this.props.article['url_id']
      },
        tag(ArticleMixButton, {
          className: bem.element('add-button'),
          checked: this.props.article.hasMix,
          onClick: this.onAddClick
        })
      )
    )
  }

  renderBottomButtons() {
    return (
      tag('div', {
        className: bem.element('bottom-buttons')
      },
        this.renderShareButton(),
        this.renderAddButton()
      )
    )
  }

  renderContent() {
    switch (this.props.article.subtype) {
      case articleSubtypes.TEXT:
        return tag([
          this.renderDetails()
        ])
      case articleSubtypes.VIDEO:
      case articleSubtypes.AUDIO:
        return tag([
          this.renderEmbed(),
          this.renderDetails()
        ])
      case articleSubtypes.IMAGE:
        return tag([
          this.renderImage(),
          this.renderDetails()
        ])
      default:
        return tag([
          this.renderDetails(),
          this.renderImage()
        ])
    }
  }

  renderCount() {
    if (!this.props.hasCount || !this.props.article.count) {
      return null
    }
    return (
      tag('a', {
        className: bem.element('count'),
        href: this.permalinkHref(),
        onClick: this.onCountClick
      },
        `${formatCount(this.props.article.count)}`
      )
    )
  }

  renderDescription() {
    if (!this.props.article.description) {
      return null
    }
    return (
      tag('a', {
        className: bem.element('description'),
        href: this.articleHref(),
        rel: 'nofollow noopener',
        target: '_blank'
      },
        this.props.article.description
      )
    )
  }

  renderDetails() {
    return (
      tag('div', {
        className: bem.element('details-container')
      },
        tag('div', {
          className: bem.element('details-top')
        },
          this.renderDomain(),
          this.renderPreviewButton(),
          this.renderMoreButton()
        ),
        this.renderTitle(),
        this.renderDescription()
      )
    )
  }

  renderDomain() {
    return (
      tag('a', {
        className: bem.element('domain'),
        href: this.domainHref(),
        onClick: this.onDomainClick
      },
        this.props.article.hostname
      )
    )
  }

  renderEmbed() {
    const { embedUrl } = this.props.article
    if (!embedUrl || !this.state.isPartiallyVisible) {
      return this.renderImage()
    }
    return (
      tag('div', {
        className: bem.element('embed-container')
      },
        this.renderImage(),
        tag(EmbeddedPlayer, {
          isDisclosed: this.state.isEmbedDisclosed,
          className: bem.element('embed'),
          src: embedUrl,
          onDisclose: this.discloseEmbed
        })
      )
    )
  }

  renderFooter() {
    return (
      tag('div', {
        className: bem.element('footer'),
        onClick: this.stopPropagation
      },
        this.renderFooterIcon(),
        tag('span', {
          className: bem.element('footer-text'),
          dangerouslySetInnerHTML: this.footerHtml()
        })
      )
    )
  }

  renderFooterIcon() {
    const user = this.footerUser()
    if (!user) {
      return null
    }
    return tag(UserAvatar, {
      className: bem.element('footer-icon'),
      href: true,
      user
    })
  }

  renderImage() {
    const {
      article: { image, md5image }
    } = this.props
    return (
      tag(LazyImage, {
        className: bem.element('image'),
        href: this.articleHref(),
        rel: 'nofollow noopener',
        target: '_blank',
        sources: [md5image, image]
      })
    )
  }

  renderMoreButton() {
    if (!this.props.onMoreClick || this.props.article.hasMix) {
      return null
    }
    return (
      tag(CircleButton, {
        className: bem.element('more-button'),
        title: 'Report this content or block the source',
        onClick: this.onMoreClick
      })
    )
  }

  renderPreviewButton() {
    if (!this.props.onPreviewClick) {
      return null
    }
    return (
      tag(CircleButton, {
        className: bem.element('preview-button'),
        href: this.permalinkHref(),
        title: 'Get more details about this post',
        onClick: this.onPreviewClick
      })
    )
  }

  renderShareButton() {
    if (!this.props.onShareClick) {
      return null
    }
    return (
      tag(CircleButton, {
        className: bem.element('share-button'),
        title: 'Share this post with a friend',
        onClick: this.onShareClick
      })
    )
  }

  renderTitle() {
    return (
      tag('a', {
        className: bem.element('title'),
        href: this.articleHref(),
        rel: 'nofollow noopener',
        target: '_blank'
      },
        this.props.article.title
      )
    )
  }

  render() {
    return (
      tag('div', {
        ref: this.ref,
        className: classNames(
          this.props.className,
          bem.block({
            // Prefer the subtype enum key in the stylesheet instead of the value
            subtype: articleSubTypesKeys[this.props.article.subtype]
          })
        ),
        dataset: {
          urlId: this.props.article['url_id']
        }
      },
        tag('div', {
          className: bem.element('inner', {
            interactive: !!this.props.onCardClick
          }),
          onClick: this.onCardClick
        },
          this.renderContent(),
          this.renderFooter(),
          this.renderCount(),
          this.renderBottomButtons()
        )
      )
    )
  }
}


ArticleCard.propTypes = {
  // Flags
  isEmbedDisclosed: PropTypes.bool,
  isHrefsEnabled: PropTypes.bool,
  hasCount: PropTypes.bool,
  // Values
  article: PropTypes.shape(ArticleModelRecord.propTypes).isRequired,
  className: PropTypes.string,
  itemIndex: PropTypes.number,
  pageContext: PropTypes.shape(PageContextRecord.propTypes),
  portalContainer: Portal.containerPropType,
  recReason: PropTypes.shape(RecReasonRecord.propTypes),
  // Callbacks needed as props
  onAddClick: PropTypes.func,
  onCardClick: PropTypes.func,
  onMoreClick: PropTypes.func,
  onPreviewClick: PropTypes.func,
  onShareClick: PropTypes.func
}

// Callbacks for event handlers
ArticleCard.eventHandlerKeys = [
  'onCardClick',
  'onCountClick',
  'onDomainClick',
  'onEmbedDisclose',
  'onPreviewClick',
  'onSeen',
  'onShareClick'
]
ArticleCard.eventHandlerKeys.forEach(key => {
  ArticleCard.propTypes[key] = PropTypes.func
})

ArticleCard.defaultProps = {
  isEmbedDisclosed: false, // Set to `false` to enable the embed curtain
  isHrefsEnabled: true,
  hasCount: true
}

module.exports = ArticleCard
